/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import cx from 'classnames';
import CheckBox from '../AZCustomComponent/FormGroup/CheckBox';
import { SmartLink as Link } from '../../utils/smartLink';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import type { FilterDataType, RefinementType, HandleFilterType } from '../../types/filterTypes';
import { pageTypes } from '../../constants/page';
import { Rating } from '../Rating/Rating';
import CustomPriceRange from '../AZCustomComponent/CustomPriceRange';
import { useCustomPriceFilterEnabled } from '../../hooks/useCustomPriceFilterEnabled';
import { useAppData } from '@/hooks/useAppData';
import FilterSubHeading from './FiltersubHeading';
import styles from './FilterDesktop/styles.module.scss';
import { priceFilterDisplayNames } from '@/components/Filter/priceFilterDisplayNames';
import Image from '@/components/NextImage';
import { usePageType } from '@/features/category/api/getPageType';
import { getCurrencySymbol } from '@/utils/priceHelpers';
import { formatPriceFilterLabel } from '@/utils/validator/priceCommaFormat';
import { ExpansionButton } from '../ExpansionButton';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';

const labelMap = {
  lblShowLess: 'label_ShelfPage_body_ShowLess',
  lblShowAll: 'hyperlink_ShelfPage_leftSideBar_ShowAll',
  lblItems: 'label_items',
  lblTo: 'labels_order_to',
  lblProducts: 'label_customerservice_contactus_products',
  lblSearch: 'label_Search_Results_Search',
};

//there's no need to cover rating = 2, rating = 1 or 'no rating' for now
const ratingFilterIds = [
  {
    id: '4000000402',
    value: 5,
    facetInternalName: '5',
  },
  {
    id: '4000000403',
    value: 4,
    facetInternalName: '4 to *',
  },
  {
    id: '4000000404',
    value: 3,
    facetInternalName: '3 to *',
  },
];

const ratingFilterIdsAces = [
  {
    id: '4000000402',
    value: 5,
    facetInternalName: '1~5~5',
  },
  {
    id: '4000000403',
    value: 4,
    facetInternalName: '2~4 TO *~4 and up',
    facetInternalNameEs: '4 y más',
  },
  {
    id: '4000000404',
    value: 3,
    facetInternalName: '3~3 TO *~3 and up',
    facetInternalNameEs: '3 y más',
  },
];

type Props = {
  classes?: {
    root?: string;
  };
  filterItem: FilterDataType;
  handleFilter: (a: HandleFilterType) => void;
  hasVehicle?: boolean;
  pageType: string;
  showFilterSubHeading?: boolean;
};

export const FilterDataRefinement = ({
  filterItem,
  handleFilter,
  hasVehicle = false,
  pageType,
  showFilterSubHeading = true,
  classes = {},
}: Props) => {
  const labels = useLabels(labelMap);
  const { acesEnabled } = useAcesFlags();
  const { appData } = useAppData();
  const { deviceType } = appData;
  const isABot = deviceType === 'bot';
  const customPriceRangeEnabled = useCustomPriceFilterEnabled();
  const expandedButtonRef = useRef<HTMLInputElement>(null);
  const isShelf = pageType?.toLowerCase().includes(pageTypes.shelf) ?? false;
  const isSearch = pageType?.toLowerCase().includes(pageTypes.search) ?? false;
  const dimensionName = filterItem?.dimensionName ?? '';
  const isPriceFilter = Object.values(priceFilterDisplayNames).includes(
    dimensionName?.toLowerCase(),
  );
  const isAvailabilityFilter = dimensionName === 'availability_options_ss';
  const isVehicleFitmentFilter =
    dimensionName === 'Vehicle Fitment' || dimensionName === 'vehicle_fitment_s';
  const refinementSize = 4;
  const refinements = filterItem?.refinements ?? [];

  const [itemRefinements, setItemRefinements] = useState<RefinementType[]>(refinements);
  const { data: pageTypeData } = usePageType();
  const isYMMShelf = !!pageTypeData?.make || !!pageTypeData?.model || !!pageTypeData?.year;

  const refinementsLength = filterItem?.refinements.length;
  const displayName = filterItem?.displayName ?? '';
  const displayNameUpperCase = displayName.toUpperCase();
  const locale = useLocale();
  const currencySymbol = getCurrencySymbol(locale);
  const [showingMore, setShowingMore] = useState(isABot);
  const filtersToDisplay = showingMore ? itemRefinements : itemRefinements.slice(0, refinementSize);

  const mapRefinements = useCallback(
    (refinements: RefinementType[]) => {
      if (isPriceFilter) {
        return refinements.map((item) => ({
          ...item,
          label: formatPriceFilterLabel(item?.label) as string,
        }));
      }
      return refinements;
    },
    [isPriceFilter],
  );

  const searchFilters = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchText = e.target.value;
    if (searchText === '') {
      setItemRefinements(mapRefinements(refinements));
      return;
    }
    const filteredRefinements = refinements.filter((refinement) => {
      if (refinement.label.toLowerCase().includes(searchText.toLowerCase())) {
        return refinement;
      }
    });
    setItemRefinements(mapRefinements(filteredRefinements));
  };

  useEffect(() => {
    setItemRefinements(mapRefinements(filterItem?.refinements));
  }, [filterItem?.refinements, mapRefinements]);

  useEffect(() => {
    if (showingMore && expandedButtonRef.current && !isABot) {
      expandedButtonRef.current.focus();
    }
  }, [showingMore, expandedButtonRef, isABot]);

  return (
    <div
      className={cx(styles.filterCategory, styles.shrinkable, {
        [classes.root || '']: classes.root,
        [styles.notShowingMore]: !showingMore,
      })}
      key={dimensionName}
      data-testid={`filter-group-${displayNameUpperCase.replace(/\s/g, '')}`}
    >
      {showFilterSubHeading && (
        <FilterSubHeading key={dimensionName} title={displayNameUpperCase} />
      )}
      {isPriceFilter && (customPriceRangeEnabled || isSearch) && (
        <div className={styles.customPriceRangeContainer}>
          <CustomPriceRange quickFilter={filterItem.quickFilter} />
        </div>
      )}
      {!isPriceFilter && refinementsLength > 15 && (
        <>
          <div className={styles.searchInputContainer}>
            <input
              name="Search"
              className={cx(styles.searchInput)}
              onChange={(event) => searchFilters(event)}
              placeholder={labels.lblSearch}
              aria-label={displayName}
              data-testid={`search-${displayNameUpperCase.replace(/\s/g, '')}`}
            />
            <Image
              className={styles.searchImage}
              src="/images/shape-search.svg"
              alt=""
              width={36}
              height={19}
            />
          </div>
        </>
      )}
      <div className={styles.filterListContainer}>
        <ul
          className={styles.bottomSpacing}
          id={`filterList${displayName}`}
          data-testid={`filterList-${displayNameUpperCase.replace(/\s/g, '')}`}
        >
          {filtersToDisplay.map((itemRefinement: RefinementType, index) => {
            const itemLabel = itemRefinement.label;
            const labelForToItemIdValue = itemLabel.includes(' ')
              ? itemLabel.split(' ').join('')
              : `${itemRefinement.label}`;
            const replaceRegex = new RegExp(`${currencySymbol}([\\d,]{4,}(?:\\.\\d+)?)`, 'g');
            const itemRefinementLabel = itemRefinement.label.replace(replaceRegex, (match) => {
              const price = parseFloat(match.replace(currencySymbol, '').replace(',', ''));
              return currencySymbol + price.toLocaleString();
            });
            const itemRefinementCount = itemRefinement?.count ?? 0;
            const itemRefinementCountLabel =
              (!hasVehicle && isVehicleFitmentFilter) || isAvailabilityFilter
                ? ''
                : ` (${itemRefinementCount})`;
            const fullLabel = `${itemRefinementLabel}${itemRefinementCountLabel}`;
            const disabled = (!isAvailabilityFilter && itemRefinementCount === 0) || isABot;
            const botSeoUrl = itemRefinement?.properties?.botsOptimizedSeoUrl;
            const filterId = itemRefinement?.properties?.filters;
            const endecaChecked =
              String(itemRefinement?.properties?.isSelected ?? 'false') === 'true';
            const ratingFilters = acesEnabled ? ratingFilterIdsAces : ratingFilterIds;
            const internalName = itemRefinement?.properties?.internalName;
            const rating = ratingFilters.find(
              (rating) =>
                rating.id === filterId ||
                ((dimensionName === 'avg_rating_f' || dimensionName === 'avg_rating_range_ss') &&
                  (rating.facetInternalName === internalName ||
                    ('facetInternalNameEs' in rating &&
                      rating.facetInternalNameEs === internalName))),
            );
            return (
              <li
                data-testid={itemLabel}
                key={`${itemLabel}-${itemRefinement.properties.botsOptimizedSeoUrl}`}
              >
                {isABot && isShelf && botSeoUrl && !isYMMShelf ? (
                  <Link to={botSeoUrl} className={styles.botLink}>
                    {fullLabel}
                  </Link>
                ) : (
                  <CheckBox
                    checked={endecaChecked}
                    variant="natural"
                    inputRef={showingMore && index === refinementSize ? expandedButtonRef : null}
                    inputProps={{
                      'data-testid': `filter-refinement-${index}`,
                    }}
                    onChange={(event: unknown, checked: boolean) => {
                      handleFilter({
                        refinement: itemRefinement,
                        dimensionName,
                        displayName,
                        checked,
                        quickFilter: filterItem.quickFilter,
                        selectionType: filterItem.quickFilter ? 'Quick Filter' : 'Standard',
                        isStandardFilter: true,
                      });
                    }}
                    label={
                      rating ? (
                        <div className={styles.ratingFilterLabelWrapper}>
                          <Rating value={rating.value} />
                          <span>&nbsp;{fullLabel}</span>
                        </div>
                      ) : (
                        fullLabel
                      )
                    }
                    id={`${
                      filterItem.quickFilter ? 'quick' : 'standard'
                    }-${dimensionName}-${labelForToItemIdValue}`}
                    status={!endecaChecked && disabled ? 'disabled' : ''}
                  />
                )}
              </li>
            );
          })}
          {itemRefinements.length > refinementSize ? (
            <ExpansionButton
              isExpanded={showingMore}
              onClick={() => setShowingMore((value) => !value)}
              className={styles.filterExpansionButton}
            />
          ) : null}
        </ul>
      </div>
    </div>
  );
};

export default FilterDataRefinement;
